import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Copy,
  Check,
  ExternalLink,
  Play,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { cn } from '../lib/utils'

export interface GWSGuide {
  service_account_email: string
  admin_console_url: string
  steps: string[]
  scopes: string[]
  scopes_copyable: string
}

interface GWSDelegationGuideProps {
  guide: GWSGuide
  onTestConnection: () => void
  testStatus?: 'idle' | 'testing' | 'success' | 'error'
  testMessage?: string
  className?: string
}

export function GWSDelegationGuide({
  guide,
  onTestConnection,
  testStatus = 'idle',
  testMessage,
  className,
}: GWSDelegationGuideProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [showScopes, setShowScopes] = useState(false)
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0]))

  const handleCopy = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch {
      // Fallback: select the text for manual copy
      console.warn('Clipboard API not available')
    }
  }, [])

  const toggleStep = useCallback((index: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }, [])

  const expandAll = useCallback(() => {
    setExpandedSteps(new Set(guide.steps.map((_, i) => i)))
  }, [guide.steps])

  const collapseAll = useCallback(() => {
    setExpandedSteps(new Set([0]))
  }, [])

  return (
    <div className={cn('bg-white rounded-xl border border-gray-200 overflow-hidden', className)}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-navy">
              Google Workspace Domain-Wide Delegation
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Follow these steps to authorize DigitalWatch to connect to this Google Workspace. Connecting is not monitoring.
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={expandAll}
              className="text-xs text-brass hover:text-brass-dark font-medium px-2 py-1 rounded hover:bg-brass/5 transition-colors"
            >
              Expand all
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={collapseAll}
              className="text-xs text-brass hover:text-brass-dark font-medium px-2 py-1 rounded hover:bg-brass/5 transition-colors"
            >
              Collapse
            </button>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Service Account Email — Copyable */}
        <div>
          <label className="block text-xs font-medium text-navy mb-1.5">
            Service Account Email
          </label>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy font-mono break-all">
              {guide.service_account_email}
            </code>
            <button
              type="button"
              onClick={() => handleCopy(guide.service_account_email, 'email')}
              className={cn(
                'tap-min flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                copiedField === 'email'
                  ? 'border-green-300 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-white text-navy hover:bg-gray-50',
              )}
              aria-label="Copy service account email"
            >
              {copiedField === 'email' ? (
                <>
                  <Check className="w-3.5 h-3.5" aria-hidden="true" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Scopes — Copyable + Expandable */}
        <div>
          <label className="block text-xs font-medium text-navy mb-1.5">
            OAuth Scopes
          </label>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy font-mono break-all line-clamp-2">
              {guide.scopes_copyable}
            </code>
            <button
              type="button"
              onClick={() => handleCopy(guide.scopes_copyable, 'scopes')}
              className={cn(
                'tap-min flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                copiedField === 'scopes'
                  ? 'border-green-300 bg-green-50 text-green-700'
                  : 'border-gray-300 bg-white text-navy hover:bg-gray-50',
              )}
              aria-label="Copy scopes"
            >
              {copiedField === 'scopes' ? (
                <>
                  <Check className="w-3.5 h-3.5" aria-hidden="true" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                  Copy
                </>
              )}
            </button>
          </div>

          {/* Expandable scopes list */}
          <button
            type="button"
            onClick={() => setShowScopes((v) => !v)}
            className="flex items-center gap-1 mt-2 text-xs text-brass hover:text-brass-dark font-medium transition-colors"
          >
            {showScopes ? (
              <>
                <ChevronUp className="w-3 h-3" aria-hidden="true" />
                Hide individual scopes
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" aria-hidden="true" />
                Show {guide.scopes.length} individual scopes
              </>
            )}
          </button>
          <AnimatePresence>
            {showScopes && (
              <motion.ul
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-2 bg-gray-50 rounded-lg border border-gray-200 p-3 space-y-1 max-h-48 overflow-y-auto">
                  {guide.scopes.map((scope, i) => (
                    <li
                      key={i}
                      className="text-xs text-gray-600 font-mono flex items-start gap-2"
                    >
                      <span className="text-brass mt-0.5">•</span>
                      {scope}
                    </li>
                  ))}
                </div>
              </motion.ul>
            )}
          </AnimatePresence>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Numbered Steps */}
        <div>
          <h4 className="text-xs font-semibold text-navy uppercase tracking-wide mb-3">
            Setup Steps
          </h4>
          <ol className="space-y-2">
            {guide.steps.map((step, index) => {
              const isExpanded = expandedSteps.has(index)
              return (
                <li
                  key={index}
                  className={cn(
                    'rounded-lg border transition-colors',
                    isExpanded
                      ? 'border-brass/30 bg-brass/5'
                      : 'border-gray-200 bg-white hover:border-gray-300',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleStep(index)}
                    className="w-full flex items-start gap-3 px-4 py-3 text-left"
                  >
                    <span
                      className={cn(
                        'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                        isExpanded
                          ? 'bg-brass text-white'
                          : 'bg-gray-200 text-gray-600',
                      )}
                    >
                      {index + 1}
                    </span>
                    <span className={cn('text-sm flex-1', isExpanded ? 'text-navy font-medium' : 'text-gray-600')}>
                      {step}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    )}
                  </button>

                  {/* Step detail / quick actions for specific steps */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-3 pt-0 flex items-center gap-2 ml-9">
                          {index === 0 && (
                            <a
                              href={guide.admin_console_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs text-brass hover:text-brass-dark font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" aria-hidden="true" />
                              Open Google Admin Console
                            </a>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              )
            })}
          </ol>
        </div>

        {/* Propagation note */}
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-blue-700">
            <span className="font-semibold">Note:</span> Changes in Google Admin Console can take
            up to <span className="font-semibold">30 minutes</span> to propagate. If the test fails
            after setup, wait for propagation to complete and try again.
          </p>
        </div>

        {/* Test Connection */}
        <div className="pt-2">
          <button
            type="button"
            onClick={onTestConnection}
            disabled={testStatus === 'testing'}
            className={cn(
              'tap-min w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-all',
              testStatus === 'success'
                ? 'bg-green-50 border border-green-300 text-green-700 hover:bg-green-100'
                : testStatus === 'error'
                  ? 'bg-red-50 border border-red-300 text-red-700 hover:bg-red-100'
                  : 'bg-navy text-white hover:bg-navy/90',
            )}
          >
            {testStatus === 'testing' && (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Testing connection...
              </>
            )}
            {testStatus === 'idle' && (
              <>
                <Play className="w-4 h-4" aria-hidden="true" />
                Test Connection
              </>
            )}
            {testStatus === 'success' && (
              <>
                <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
                Connection successful
              </>
            )}
            {testStatus === 'error' && (
              <>
                <AlertTriangle className="w-4 h-4" aria-hidden="true" />
                Connection failed — Retry
              </>
            )}
          </button>

          {/* Test result message */}
          <AnimatePresence>
            {testMessage && testStatus !== 'testing' && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={cn(
                  'mt-2 text-xs text-center',
                  testStatus === 'success' ? 'text-green-600' : 'text-red-600',
                )}
              >
                {testMessage}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
